import type { PointContour } from '../point-contour';
import type { PolygonClosedContour } from '../polygon-closed-contour';
import type { Point, PointArray } from '../types/contour-definition-array-type';

import { getPolygonCentroid } from './getPolygonCentroid';

export function createSaxChordLines(
  endoContour: PolygonClosedContour,
  epiContour: PolygonClosedContour,
  insertionPoint: PointContour,
  centerlineContour: PolygonClosedContour,
  segments: number = 6,
  shiftStartByHalfSegment: boolean = false,
): PointArray[] {
  const endo = endoContour.getPolygon();
  const epi = epiContour.getPolygon();
  const insertion = insertionPoint.getPolygon();

  // centroid
  const centerline = centerlineContour.getPolygon();
  const centroid = getPolygonCentroid(centerline);

  const equalAnglePoints = generateEqualArcLengthPointsWithCentroid(
    centerline,
    centroid,
    insertion[0],
    segments,
    shiftStartByHalfSegment,
  );

  /**
   * Find intersection point between two line segments
   * @param p1 First point of first line segment
   * @param p2 Second point of first line segment
   * @param p3 First point of second line segment
   * @param p4 Second point of second line segment
   * @returns Intersection point or null if no intersection
   */
  function lineSegmentIntersection(
    p1: Point,
    p2: Point,
    p3: Point,
    p4: Point,
  ): Point | null {
    const denominator
      = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);

    // Lines are parallel or collinear
    if (Math.abs(denominator) < 1e-10) return null;

    const t
      = ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x))
        / denominator;
    const u
      = -((p1.x - p2.x) * (p1.y - p3.y) - (p1.y - p2.y) * (p1.x - p3.x))
        / denominator;

    // Check if intersection is within both line segments
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
      return {
        x: p1.x + t * (p2.x - p1.x),
        y: p1.y + t * (p2.y - p1.y),
      };
    }

    return null;
  }

  /**
   * Check if two vectors are pointing in the same general direction
   */
  function sameDirection(
    v1x: number,
    v1y: number,
    v2x: number,
    v2y: number,
  ): boolean {
    // Dot product is positive when vectors are in the same general direction
    return v1x * v2x + v1y * v2y > 0;
  }

  /**
   * Find all intersection points between a ray and a contour
   */
  function findAllRayContourIntersections(
    centroidIn: Point,
    throughPoint: Point,
    contour: PointArray,
  ): Point[] {
    // Calculate direction vector
    const dirX = throughPoint.x - centroidIn.x;
    const dirY = throughPoint.y - centroidIn.y;

    // Normalize and extend the ray to ensure it's long enough to intersect
    const rayLength = Math.sqrt(dirX * dirX + dirY * dirY);
    const normalizedDirX = dirX / rayLength;
    const normalizedDirY = dirY / rayLength;

    // Create a ray endpoint far beyond the contour
    const extensionFactor = 10000; // Should be large enough to go beyond the contour
    const rayEnd = {
      x: centroidIn.x + normalizedDirX * extensionFactor,
      y: centroidIn.y + normalizedDirY * extensionFactor,
    };

    // Store all intersections
    const intersections: Point[] = [];

    // Check intersection with each edge of the contour
    for (let i = 0; i < contour.length; i++) {
      const p1 = contour[i];
      const p2 = contour[(i + 1) % contour.length]; // Loop back to first point for the last edge

      // Calculate intersection between ray and edge
      const intersection = lineSegmentIntersection(centroidIn, rayEnd, p1, p2);

      if (intersection) {
        // Only add intersections in the direction of the ray
        if (
          sameDirection(
            dirX,
            dirY,
            intersection.x - centroidIn.x,
            intersection.y - centroidIn.y,
          )
        ) {
          intersections.push(intersection);
        }
      }
    }

    return intersections;
  }

  const chordLines = [] as PointArray[];
  for (let i = 0; i < equalAnglePoints.length; i++) {
    // Get all intersections along this ray
    const endoIntersections = findAllRayContourIntersections(
      centroid,
      equalAnglePoints[i],
      endo,
    );
    const epiIntersections = findAllRayContourIntersections(
      centroid,
      equalAnglePoints[i],
      epi,
    );

    if (endoIntersections.length > 0 && epiIntersections.length > 0) {
      // Find the pair with shortest distance between endo and epi
      let shortestChord: PointArray | null = null;
      let minChordLength = Infinity;

      for (const endoPoint of endoIntersections) {
        for (const epiPoint of epiIntersections) {
          const dx = epiPoint.x - endoPoint.x;
          const dy = epiPoint.y - endoPoint.y;
          const chordLength = dx * dx + dy * dy; // squared distance

          if (chordLength < minChordLength) {
            minChordLength = chordLength;
            shortestChord = [endoPoint, epiPoint];
          }
        }
      }

      if (shortestChord) {
        chordLines.push(shortestChord);
      }
    }
  }

  return chordLines;
}

function generateEqualArcLengthPointsWithCentroid(
  centerline: PointArray,
  centroid: Point,
  insertion: Point,
  segments: number,
  shiftStartByHalfSegment: boolean = false,
): PointArray {
  if (centerline.length < 3 || segments <= 1) return centerline;

  // Calculate the insertion point angle relative to centroid
  const insertionAngle = Math.atan2(
    insertion.y - centroid.y,
    insertion.x - centroid.x,
  );

  // Calculate angles and distances relative to the centroid
  const pointsWithAngles = centerline.map((point) => {
    let angle = Math.atan2(point.y - centroid.y, point.x - centroid.x);
    // Normalize angles relative to insertion point
    angle = (angle - insertionAngle + 2 * Math.PI) % (2 * Math.PI);
    const distance = Math.sqrt(
      (point.x - centroid.x) ** 2 + (point.y - centroid.y) ** 2,
    );
    return { ...point, angle, distance };
  });

  // Sort points by angle
  pointsWithAngles.sort((a, b) => a.angle - b.angle);

  // Generate evenly spaced angles
  const angleStep = (2 * Math.PI) / segments;
  const startOffset = shiftStartByHalfSegment ? angleStep / 2 : 0;
  const result: PointArray = [];

  for (let i = 0; i < segments; i++) {
    const targetAngle = i * angleStep + startOffset; // go clockwise

    // Find the two closest points surrounding the target angle
    let lower = pointsWithAngles[0];
    let upper = pointsWithAngles[0];
    for (let j = 1; j < pointsWithAngles.length; j++) {
      if (pointsWithAngles[j].angle >= targetAngle) {
        lower = pointsWithAngles[j - 1];
        upper = pointsWithAngles[j];
        break;
      }
    }

    // Handle wrap-around case
    if (targetAngle > upper.angle) {
      lower = pointsWithAngles[pointsWithAngles.length - 1];

      upper = pointsWithAngles[0];
    }

    // Interpolate within the segment
    const t
      = (targetAngle - lower.angle)
        / ((upper.angle - lower.angle + 2 * Math.PI) % (2 * Math.PI) || 1);
    const interpolatedDistance
      = lower.distance + t * (upper.distance - lower.distance);

    // Convert back to global angle space and compute the final point's position
    const globalAngle = targetAngle + insertionAngle;
    const interpolatedPoint = {
      x: centroid.x + interpolatedDistance * Math.cos(globalAngle),
      y: centroid.y + interpolatedDistance * Math.sin(globalAngle),
    };

    result.push(interpolatedPoint);
  }

  return result;
}
